import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  getMe,
  getCredits,
  getScannerPrefs,
  getCompatPrefs,
  getAppConfig,
} from '../api/endpoints';
import type { Me, PrefsOut, CompatPrefsOut } from '../api/endpoints';
import type { Credits, AppConfig } from '../types/api';
import { getAuthToken, isTokenExpired, establishMediaSession } from '../api/config';

const DEFAULT_DISCLOSURE_TEXT =
  'Kivo earns a commission for purchases made through links on this site.';

export type Plan = 'free' | 'premium';

export interface UserContextValue {
  ready: boolean;
  authed: boolean;
  user: Me | null;
  /** Dual-balance credit wallet (monthly + surplus) for the current user. */
  credits: Credits | null;
  plan: Plan;
  scannerPrefs: PrefsOut | null;
  compatPrefs: CompatPrefsOut | null;
  refresh: () => Promise<void>;
  /** Re-fetch just the credit balances (call after a metered action). */
  refreshCredits: () => Promise<void>;
  refreshPrefs: () => Promise<void>;
  /** Credit cost for a feature, or null when unknown. Always sourced from the API. */
  costFor: (featureKey: string) => number | null;
  /** True when total credits cover the feature's cost. */
  canAfford: (featureKey: string) => boolean;
  /** Whether to render the affiliate disclosure line under the Buy button. */
  showAffiliateDisclosure: boolean;
  /** Copy for the affiliate disclosure line (backend-provided, with a default). */
  disclosureText: string;
  /**
   * Whether the backend is currently accepting new signups. Presentation
   * only: fails OPEN (true) when the config fetch hasn't landed or the field
   * is missing, so a config blip never hides a working signup form. The
   * server remains the real, fail-closed gate — it will still reject with
   * 403 registrations_closed if this client-side read is stale.
   */
  registrationOpen: boolean;
  /**
   * Whether the backend currently allows logins (login_mode == "open").
   * Same fail-open contract as `registrationOpen` — this only drives UI
   * copy/disabling, never the actual authorization decision.
   */
  loginEnabled: boolean;
}

const UserContext = createContext<UserContextValue | null>(null);

function planFromCredits(credits: Credits | null): Plan {
  const name = (credits?.plan_name ?? '').toLowerCase();
  if (!name) return 'free';
  if (name === 'free' || name === 'basic') return 'free';
  return 'premium';
}

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<Me | null>(null);
  const [credits, setCredits] = useState<Credits | null>(null);
  const [scannerPrefs, setScannerPrefs] = useState<PrefsOut | null>(null);
  const [compatPrefs, setCompatPrefs] = useState<CompatPrefsOut | null>(null);
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const inflight = useRef(false);

  const authed = (() => {
    const tok = getAuthToken();
    return !!tok && !isTokenExpired(tok);
  })();

  const refreshCredits = useCallback(async () => {
    const tok = getAuthToken();
    if (!tok || isTokenExpired(tok)) return;
    try {
      const c = await getCredits();
      setCredits(c);
    } catch {
      /* keep last-known balances on transient failure */
    }
  }, []);

  const refreshPrefs = useCallback(async () => {
    if (!authed) return;
    const [sp, cp] = await Promise.allSettled([getScannerPrefs(), getCompatPrefs()]);
    if (sp.status === 'fulfilled') setScannerPrefs(sp.value);
    if (cp.status === 'fulfilled') setCompatPrefs(cp.value);
  }, [authed]);

  const refresh = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const tok = getAuthToken();
      if (!tok || isTokenExpired(tok)) {
        setUser(null);
        setCredits(null);
        setScannerPrefs(null);
        setCompatPrefs(null);
        setReady(true);
        return;
      }
      // Establish the httpOnly media-session cookie so <img> requests to
      // ownership-checked image endpoints are authenticated (covers fresh
      // page loads of already-logged-in users and post-login refreshes).
      await establishMediaSession();
      const [meRes, creditsRes] = await Promise.allSettled([getMe(), getCredits()]);
      if (meRes.status === 'fulfilled') setUser(meRes.value);
      if (creditsRes.status === 'fulfilled') setCredits(creditsRes.value);
      await refreshPrefs();
      setReady(true);
    } finally {
      inflight.current = false;
    }
  }, [refreshPrefs]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Public per-platform app config (affiliate disclosure flag). Fetched once on
  // mount; independent of auth since /config is public and non-sensitive.
  useEffect(() => {
    let active = true;
    getAppConfig()
      .then((cfg) => { if (active) setAppConfig(cfg); })
      .catch(() => { /* non-fatal: disclosure just stays hidden */ });
    return () => { active = false; };
  }, []);

  // Keep the credit balance from going stale: re-fetch on a slow interval and
  // whenever the tab regains focus/visibility. This drives the live header
  // credit badge without requiring a manual reload.
  useEffect(() => {
    if (!authed) return;
    const POLL_MS = 60_000;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refreshCredits();
    }, POLL_MS);
    const onFocus = () => { void refreshCredits(); };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshCredits();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [authed, refreshCredits]);

  const costFor = useCallback(
    (featureKey: string): number | null => {
      const hit = credits?.costs.find((c) => c.feature_key === featureKey);
      return hit ? hit.credit_cost : null;
    },
    [credits],
  );

  const canAfford = useCallback(
    (featureKey: string): boolean => {
      const cost = credits?.costs.find((c) => c.feature_key === featureKey)?.credit_cost;
      if (cost == null) return true; // unknown cost => don't block client-side
      return (credits?.total ?? 0) >= cost;
    },
    [credits],
  );

  const value = useMemo<UserContextValue>(() => ({
    ready,
    authed,
    user,
    credits,
    plan: planFromCredits(credits),
    scannerPrefs,
    compatPrefs,
    refresh,
    refreshCredits,
    refreshPrefs,
    costFor,
    canAfford,
    showAffiliateDisclosure: !!appConfig?.show_affiliate_disclosure,
    disclosureText: appConfig?.disclosure_text ?? DEFAULT_DISCLOSURE_TEXT,
    // Fail OPEN: a failed/pending config fetch (appConfig null) or a missing
    // field on an older backend must never lock users out of a working auth
    // form. This is presentation only — the server enforces the real gate.
    registrationOpen: appConfig?.registration_open ?? true,
    loginEnabled: appConfig?.login_enabled ?? true,
  }), [ready, authed, user, credits, scannerPrefs, compatPrefs, refresh, refreshCredits, refreshPrefs, costFor, canAfford, appConfig]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within UserProvider');
  return ctx;
}

export function useOptionalUser(): UserContextValue | null {
  return useContext(UserContext);
}
